/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";

const T = {
  ink:        "#0F0F0F",
  jade:       "#0F6E56",
  jadeMid:    "#1D9E75",
  saffron:    "#D97706",
  slate:      "#6B7280",
  border:     "rgba(0,0,0,0.1)",
  white:      "#FFFFFF",
};

export default function Signup() {
  const { signup } = useAuth() as any;
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail]                     = useState("");
  const [password, setPassword]               = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError]                     = useState("");
  const [loading, setLoading]                 = useState(false);

  useEffect(() => {
    if (currentUser) navigate("/welcome");
  }, [currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await signup(email, password);
      navigate("/");
    } catch (err: any) {
      if (err?.code === "auth/email-already-in-use") {
        setError("An account with this email already exists.");
      } else {
        setError("Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", boxSizing: "border-box",
    padding: "13px 15px", borderRadius: 10,
    border: `1.5px solid ${T.border}`,
    fontSize: 15, color: T.ink, outline: "none",
    background: "#FAFAFA", fontFamily: "inherit",
    transition: "border-color 0.15s",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FAFAF8 0%,#F0FBF6 50%,#FFFBEB 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: "'Inter',system-ui,sans-serif",
      position: "relative", overflow: "hidden",
    }}>
      {/* Background orbs */}
      <div style={{ position:"absolute", width:340, height:340, borderRadius:"50%", top:"5%", right:"5%",
        background:"radial-gradient(circle,rgba(29,158,117,0.08) 0%,transparent 70%)" }} />
      <div style={{ position:"absolute", width:260, height:260, borderRadius:"50%", bottom:"8%", left:"5%",
        background:"radial-gradient(circle,rgba(217,119,6,0.07) 0%,transparent 70%)" }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: "100%", maxWidth: 420,
          background: T.white, borderRadius: 20,
          padding: "34px 32px 28px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.08)",
          border: "1px solid rgba(0,0,0,0.06)",
          position: "relative",
        }}
      >
        {/* Already have an account? tab row */}
        <div style={{
          display: "flex", background: "#F3F4F6",
          borderRadius: 10, padding: 3, marginBottom: 26,
        }}>
          <button
            onClick={() => navigate("/login")}
            style={{
              flex: 1, padding: "8px 0", border: "none", borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: "inherit",
              background: "transparent", color: T.slate,
              transition: "all 0.15s",
            }}
          >
            Sign in
          </button>
          <button
            style={{
              flex: 1, padding: "8px 0", border: "none", borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: "default",
              fontFamily: "inherit",
              background: T.white, color: T.ink,
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
          >
            Create account
          </button>
        </div>

        <p style={{
          fontFamily: "'Georgia','Times New Roman',serif",
          fontSize: 22, fontWeight: 700, color: T.ink, margin: "0 0 4px",
        }}>Get started</p>
        <p style={{ fontSize: 14, color: T.slate, margin: "0 0 24px" }}>
          Create your MySatsang account — it's free.
        </p>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                background: "#FEF2F2", border: "1px solid #FECACA",
                borderRadius: 8, padding: "10px 14px",
                fontSize: 13, color: "#B91C1C", marginBottom: 14,
                overflow: "hidden",
              }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            type="email" placeholder="Email address"
            value={email} onChange={e => setEmail(e.target.value)}
            required style={inputStyle}
            onFocus={e => (e.target.style.borderColor = T.jadeMid)}
            onBlur={e => (e.target.style.borderColor = T.border)}
          />
          <input
            type="password" placeholder="Password (min. 6 characters)"
            value={password} onChange={e => setPassword(e.target.value)}
            required style={inputStyle}
            onFocus={e => (e.target.style.borderColor = T.jadeMid)}
            onBlur={e => (e.target.style.borderColor = T.border)}
          />
          <input
            type="password" placeholder="Confirm password"
            value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
            required style={inputStyle}
            onFocus={e => (e.target.style.borderColor = T.jadeMid)}
            onBlur={e => (e.target.style.borderColor = T.border)}
          />

          {/* Password strength hint */}
          {password.length > 0 && (
            <div style={{ display: "flex", gap: 4 }}>
              {[1, 2, 3].map(n => (
                <div key={n} style={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: password.length >= n * 4
                    ? n === 1 ? "#EF4444" : n === 2 ? T.saffron : T.jadeMid
                    : "#E5E7EB",
                  transition: "background 0.3s",
                }} />
              ))}
              <span style={{ fontSize: 11, color: T.slate, alignSelf: "center", marginLeft: 4 }}>
                {password.length < 4 ? "Weak" : password.length < 8 ? "Fair" : "Strong"}
              </span>
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              marginTop: 6,
              background: loading ? "#6B7280" : T.ink,
              color: T.white, border: "none", borderRadius: 10,
              padding: "13px 0", fontSize: 15, fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "inherit", letterSpacing: "0.01em",
              transition: "background 0.15s",
            }}
          >
            {loading ? "Creating account…" : "Start your journey"}
          </button>
        </form>

        <p style={{
          fontSize: 12, color: "#9CA3AF", textAlign: "center",
          marginTop: 16, lineHeight: 1.5,
        }}>
          By creating an account you agree to use this platform
          respectfully for spiritual study.
        </p>
      </motion.div>
    </div>
  );
}